import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import L, { icon, latLngBounds, LeafletEventHandlerFnMap } from "leaflet";
import { Marker, Polyline, Popup, Tooltip, useMap } from "react-leaflet";
import { Tooltip as MuiTooltip } from "@mui/material";
import { useRecoilValue } from 'recoil';

import { UserType, VehicleGridType } from "../../../../types/types";
import PopUpContent from "./PopUpContent";
import { formatString, findNearestVehicle, getVehicleIgnitionStatus } from "../../../../functions/functions";
import { userAtom } from '../../../../atoms/atoms';

type NearbyRouteInfo = {
    origin: VehicleGridType;
    nearest: VehicleGridType;
    distanceText: string;
    durationText: string;
};

export type OpenPositionsMarkersHandle = {
    focusOnNearbyFromPoint: (origin: VehicleGridType) => void;
};

// O popup enquadra o veículo só ao abrir. Depois disso o usuário move o mapa livremente: com autoPan ligado,
// cada posição nova do veículo (e o keepInView, ao fim de cada arrasto/zoom) puxava o mapa de volta.
const popupFrameOnOpenHandlers: LeafletEventHandlerFnMap = {
    add: (event) => { (event.target as L.Popup).options.autoPan = false; },
    remove: (event) => { (event.target as L.Popup).options.autoPan = true; },
};

const OpenPositionsMarkers = forwardRef<OpenPositionsMarkersHandle, {
    gridSelected?: VehicleGridType;
    positions: VehicleGridType[];
    children?: JSX.Element;
    setActions: (item: VehicleGridType) => void;
    setValue: (value: number) => void;
    useTransparentBackGround?: boolean;
    activeNearbyOriginId?: string | null;
    onNearbyRouteChange?: (info: NearbyRouteInfo | null) => void;
}>(({
    positions,
    setActions,
    setValue,
    gridSelected,
    useTransparentBackGround: propUseTransparentBackGround,
    activeNearbyOriginId,
    onNearbyRouteChange
}, ref) => {
    const getCurrentUser = useRecoilValue<UserType>(userAtom);
    const useTransparentBackGround = propUseTransparentBackGround ?? getCurrentUser?.configs?.customerSettings?.useTransparentBackground;

    const markersRef = useRef<{ [key: string]: L.Marker }>({});
    const map = useMap();
    const [routeCoords, setRouteCoords] = useState<[number, number][]>([]);

    const markerIcon = useCallback((type: string) => icon({
        iconSize: [35, 35],
        iconAnchor: [10, 41],
        popupAnchor: [2, -40],
        iconUrl: require(`../../../../assets/files/cars/png/${formatString(type)}.png`),
        shadowUrl: ""
    }), []);

    const focusOnMarker = useCallback((id: string) => {
        const marker = markersRef.current[id];
        if (!marker) return;
        const latlng = marker.getLatLng();
        map.flyTo(latlng, 16, { duration: 0.5 });
    }, [map]);

    const getClicked = useCallback(() => {
        const id = String(gridSelected?.id ?? "");
        const marker = markersRef.current[id];
        if (marker) {
            focusOnMarker(id);
            marker.openPopup();
        }
    }, [focusOnMarker, gridSelected?.id]);

    useEffect(() => {
        if (gridSelected?.id) getClicked();
    }, [getClicked, gridSelected?.id]);

    // Limpa a rota quando o card de resumo for fechado no DashMap
    useEffect(() => {
        if (!activeNearbyOriginId) setRouteCoords([]);
    }, [activeNearbyOriginId]);

    const focusOnNearby = useCallback(async (origin: VehicleGridType) => {
        const nearest = findNearestVehicle(origin as any, positions as any);
        if (!nearest || !origin.position || !nearest.position) return;

        try {
            const url = `https://router.project-osrm.org/route/v1/driving/${origin.position.lng},${origin.position.lat};${nearest.position.lng},${nearest.position.lat}?overview=full&geometries=geojson`;
            const res = await fetch(url);
            const data = await res.json();
            const route = data?.routes?.[0];
            if (!route) return;

            const coords: [number, number][] = route.geometry.coordinates.map(
                (c: [number, number]) => [c[1], c[0]]
            );
            setRouteCoords(coords);

            // Expande o zoom mostrando os veículos e o trajeto
            const bounds = latLngBounds(coords);
            map.fitBounds(bounds, { padding: [80, 80] });

            const distanceText = `${(route.distance / 1000).toFixed(1)} km`;
            const durationMinutes = Math.round(route.duration / 60);
            const durationText = durationMinutes < 60
                ? `${durationMinutes} min`
                : `${Math.floor(durationMinutes / 60)}h ${durationMinutes % 60}min`;

            onNearbyRouteChange?.({ origin, nearest, distanceText, durationText });
        } catch (error) {
            console.error("Erro ao calcular rota OSRM:", error);
        }
    }, [positions, map, onNearbyRouteChange]);

    useImperativeHandle(ref, () => ({
        focusOnNearbyFromPoint: focusOnNearby,
    }), [focusOnNearby]);

    return (
        <>
            {routeCoords.length > 0 && (
                <Polyline positions={routeCoords} color="#FF0000" weight={5} opacity={0.85} />
            )}

            {positions.map((item: any) => {
                const status = getVehicleIgnitionStatus(item?.attributes, item?.position?.protocol, item?.idle);
                return (
                    <Marker
                        key={item.id}
                        ref={(ref) => {
                            if (ref) markersRef.current[String(item.id)] = ref;
                        }}
                        position={{ lat: item?.position?.lat, lng: item?.position?.lng }}
                        icon={markerIcon(item?.vehicleTypeName)}
                    >
                        <Tooltip
                            direction="center"
                            offset={[0, 10]}
                            position={{ lat: item?.position?.lat, lng: item?.position?.lng }}
                            permanent
                            interactive
                            className={`vehicle-marker-tooltip${useTransparentBackGround ? ' tooltip-transparent' : ''}`}
                        >
                            <MuiTooltip title={status.label} arrow>
                                <div role="group" aria-label={`${item.name} - ${item.vehiclePlate}: ${status.label}`} tabIndex={0}>
                                    {!useTransparentBackGround && (
                                        <div style={{
                                            backgroundColor: status.color,
                                            color: 'white',
                                            height: "10px",
                                            width: "10px",
                                            padding: '4px',
                                            borderRadius: '4px',
                                            fontWeight: 'bold',
                                            fontSize: '12px'
                                        }} />
                                    )}
                                    {`${item.name} - ${item.vehiclePlate}`}
                                </div>
                            </MuiTooltip>
                        </Tooltip>
                        <Popup
                            autoPan
                            autoPanPadding={[24, 24]}
                            eventHandlers={popupFrameOnOpenHandlers}
                            closeOnClick
                            className="vehicle-leaflet-popup"
                            minWidth={0}
                            maxWidth={440}
                        >
                            <PopUpContent
                                selected={item as any}
                                setActions={() => setActions(item)}
                                setValue={setValue}
                                onFocusNearby={() => focusOnNearby(item)}
                            />
                        </Popup>
                    </Marker>
                );
            })}
        </>
    );
});

OpenPositionsMarkers.displayName = "OpenPositionsMarkers";

export default OpenPositionsMarkers;
